import * as THREE from 'three';

const vertexShader = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

const fragmentShader = `
precision highp float;
varying vec2 vUv;

uniform vec2 uResolution;
uniform vec4 uGlassRect; // x: left, y: top, z: width, w: height (screen coords)
uniform float uRadius;
uniform float uBezel;
uniform float uThickness;
uniform float uIOR;
uniform float uBlur;
uniform float uSpecular;
uniform float uTint;
uniform sampler2D uBgTex;
uniform float uBgAspect;
uniform float uHasBgTex;

float sdRoundedRect(vec2 p, vec2 halfSize, float r) {
  vec2 q = abs(p) - halfSize + r;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

float surfaceHeight(float t) {
  float s = 1.0 - t;
  return pow(1.0 - s*s*s*s, 0.25);
}

vec3 sampleBg(vec2 screenUV) {
  if (uHasBgTex < 0.5) {
    return vec3(0.0);
  }
  float screenAspect = uResolution.x / uResolution.y;
  vec2 uv;

  float texX = screenUV.x;
  float texY = 1.0 - screenUV.y;

  if (screenAspect > uBgAspect) {
    uv.x = texX;
    uv.y = (texY - 0.5) * (uBgAspect / screenAspect) + 0.5;
  } else {
    uv.x = (texX - 0.5) * (screenAspect / uBgAspect) + 0.5;
    uv.y = texY;
  }

  return texture2D(uBgTex, clamp(uv, 0.0, 1.0)).rgb;
}

vec3 sampleBgBlurred(vec2 uv, float radius) {
  if (radius < 0.5 || uHasBgTex < 0.5) return sampleBg(uv);
  vec3 sum = vec3(0.0);
  vec2 px = 1.0 / uResolution;
  vec2 offsets[8];
  offsets[0] = vec2(-0.7, -0.7);
  offsets[1] = vec2( 0.7, -0.7);
  offsets[2] = vec2(-0.7,  0.7);
  offsets[3] = vec2( 0.7,  0.7);
  offsets[4] = vec2(-1.0,  0.0);
  offsets[5] = vec2( 1.0,  0.0);
  offsets[6] = vec2( 0.0, -1.0);
  offsets[7] = vec2( 0.0,  1.0);
  for (int i = 0; i < 8; i++) {
    sum += sampleBg(uv + offsets[i] * radius * px);
  }
  return sum / 8.0;
}

void main() {
  vec2 halfSize = uGlassRect.zw * 0.5;
  vec2 p = vec2(vUv.x - 0.5, (1.0 - vUv.y) - 0.5) * uGlassRect.zw;
  float r = min(uRadius, min(halfSize.x, halfSize.y));

  float sd = sdRoundedRect(p, halfSize, r);

  if (sd > 0.0) {
    discard;
  }

  float distFromEdge = -sd;
  float bezel = max(min(uBezel, r - 1.0), 1.0);
  float t = clamp(distFromEdge / bezel, 0.0, 1.0);

  float h = surfaceHeight(t);
  float dt = 0.001;
  float h2 = surfaceHeight(min(t + dt, 1.0));
  float dh = (h2 - h) / dt;

  float slopeAngle = atan(dh * (uThickness / bezel) * 0.3);
  float sinR = sin(slopeAngle) / max(uIOR, 1.0);
  sinR = clamp(sinR, -1.0, 1.0);
  float thetaR = asin(sinR);
  float rawDisp = h * uThickness * (tan(slopeAngle) - tan(thetaR));
  float maxD = min(uGlassRect.z, uGlassRect.w) * 0.15;
  float displacement = clamp(rawDisp * 0.35, -maxD, maxD);

  vec2 grad;
  float eps = 0.5;
  grad.x = sdRoundedRect(p + vec2(eps, 0.0), halfSize, r) - sd;
  grad.y = sdRoundedRect(p + vec2(0.0, eps), halfSize, r) - sd;
  float gradLen = length(grad);
  // Guard against normalize(vec2(0)) — produces NaN on Safari GPU, causing center line artifact
  grad = gradLen > 0.0001 ? grad / gradLen : vec2(0.0);

  vec2 screenPx = uGlassRect.xy + vec2(vUv.x, 1.0 - vUv.y) * uGlassRect.zw;
  vec2 screenUV = screenPx / uResolution;
  vec2 offset = -grad * (displacement / uResolution);
  vec2 refractedUV = screenUV + offset;

  vec3 color = sampleBgBlurred(refractedUV, uBlur);

  // Soft natural specular rim highlight
  vec2 lightDir = normalize(vec2(0.5, -0.7));
  float rimDot = max(dot(grad, lightDir), 0.0);
  float rimFalloff = (1.0 - smoothstep(0.0, bezel * 0.4, distFromEdge));
  float specHighlight = pow(rimDot, 2.0) * rimFalloff;
  color += vec3(specHighlight * uSpecular * 0.6);

  // Subtle natural edge gradient
  float innerShadow = 1.0 - smoothstep(0.0, bezel * 0.5, distFromEdge);
  color *= mix(1.0, 0.88, innerShadow * 0.18);

  color = mix(color, vec3(1.0), uTint);

  float alpha = smoothstep(0.0, 1.0, distFromEdge);
  gl_FragColor = vec4(color, alpha);
}
`;

export class WebGLGlassRenderer {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private material: THREE.ShaderMaterial;
  private bgTexture: THREE.Texture | null = null;
  private currentBgUrl = '';

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      premultipliedAlpha: false,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        uGlassRect: { value: new THREE.Vector4(0, 0, 300, 200) },
        uRadius: { value: 36 },
        uBezel: { value: 50 },
        uThickness: { value: 90 },
        uIOR: { value: 3.0 },
        uBlur: { value: 0.0 },
        uSpecular: { value: 0.3 },
        uTint: { value: 0.0 },
        uBgTex: { value: null },
        uBgAspect: { value: 16 / 9 },
        uHasBgTex: { value: 0.0 },
      },
      transparent: true,
      depthTest: false,
    });

    this.scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material));
  }

  private loadingId = 0;

  public loadBackground(url: string, force = false) {
    if (!url) return;
    if (!force && url === this.currentBgUrl) return;
    this.currentBgUrl = url;

    // Increment loading ID to discard stale loads
    const thisLoadId = ++this.loadingId;

    this.loadBackgroundAsync(url, thisLoadId).catch(() => {
      if (this.loadingId === thisLoadId) {
        this.material.uniforms.uHasBgTex.value = 0.0;
      }
    });
  }

  private async loadBackgroundAsync(url: string, loadId: number) {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
      }
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load background image from ${url}`));
      img.src = url;
    });

    if (this.loadingId !== loadId) return; // Stale request

    const W = 1920;
    const H = 1080;
    const offscreen = document.createElement('canvas');
    offscreen.width = W;
    offscreen.height = H;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(img, 0, 0, W, H);

    if (this.loadingId !== loadId) return; // Stale request

    const tex = new THREE.CanvasTexture(offscreen);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.needsUpdate = true;

    if (this.bgTexture) {
      this.bgTexture.dispose();
    }
    this.bgTexture = tex;
    this.material.uniforms.uBgTex.value = tex;
    this.material.uniforms.uHasBgTex.value = 1.0;
    this.material.uniforms.uBgAspect.value = W / H;
    this.render();
  }

  public invalidateBackground() {
    this.currentBgUrl = '';
  }

  private lastW = 0;
  private lastH = 0;

  public update(
    rect: DOMRect,
    options: {
      radius: number;
      thickness: number;
      bezel: number;
      ior: number;
      blur: number;
      specular: number;
      tint: number;
    }
  ) {
    const w = Math.max(Math.round(rect.width), 1);
    const h = Math.max(Math.round(rect.height), 1);

    if (this.lastW !== w || this.lastH !== h) {
      this.lastW = w;
      this.lastH = h;
      this.renderer.setSize(w, h, false);
    }

    const u = this.material.uniforms;
    u.uResolution.value.set(window.innerWidth, window.innerHeight);
    u.uGlassRect.value.set(rect.left, rect.top, rect.width, rect.height);
    u.uRadius.value = options.radius;
    u.uThickness.value = options.thickness;
    u.uBezel.value = options.bezel;
    u.uIOR.value = options.ior;
    u.uBlur.value = options.blur;
    u.uSpecular.value = options.specular;
    u.uTint.value = options.tint;

    this.render();
  }

  public render() {
    this.renderer.render(this.scene, this.camera);
  }

  public destroy() {
    this.bgTexture?.dispose();
    this.renderer.dispose();
  }
}
